/*
 * 작성자 : 김광민
 * 날짜 : 2024.07.04
 * 기능 : 상황판에서 가로 사이즈 5 기상종합 컴포넌트 중 현재날씨
 */

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import i18n from '../../translation/i18n';
import { useEffect, useState } from 'react';
import { ReactComponent as SnowFlower } from '../../assets/images/icons/snow-flower.svg';
import { ReactComponent as SnowIce30 } from '../../assets/images/icons/snow-ice30.svg';
import { ReactComponent as SnowBall } from '../../assets/images/icons/snow-ball.svg';
import { BlurSvgIcon, CloudySvgIcon, LittleCloudySvgIcon, RainSvgIcon, ShowerSvgIcon, SleetSvgIcon, SnowSvgIcon, SunnySvgIcon } from '../Icons';

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding: 1.5rem 0 1.5rem 1.5rem;

  .weatherError {
    position: absolute;
    width: 100%;
    height: 100%;
    > div {
      width: inherit;
      > p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: inherit;
        padding: 0.25rem 0;
        color: #fff;
        background-color: rgba(0, 0, 0, 0.4);
        border-radius: 0;
        gap: 0.25rem;
      }
    }
  }

  * {
    color: var(--widget-text-secondary);
    line-height: normal;
  }
  .unit {
    font-weight: 200;
    margin-left: 0.25rem;
  }
  .seperator {
    height: 1.25rem;
    border-left: 0.125rem solid var(--alpha-25);
    margin-left: 0.5rem;
  }
  .title {
    height: 4rem;
    width: 100%;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    font-weight: 700;
  }
  .header {
    flex-grow: 1;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    .tempNow {
      font-size: 3.5rem;
      font-weight: 600;
      margin: 0.5rem 0;
    }
    .discrict {
      font-size: 3rem;
      font-weight: 400;
      &:before {
        content: '•';
        margin-right: 1rem;
        color: var(--alpha-50);
      }
    }
  }
  .info {
    z-index: 100;
    width: 100%;
    display: flex;
    flex-direction: column;
    .tempNow {
      font-size: 3.5rem;
      font-weight: 600;
      margin-top: 2.5rem;
    }
    .discrict {
      font-size: 3rem;
      font-weight: 400;
      &:before {
        content: '•';
        margin-right: 1rem;
        color: var(--alpha-50);
      }
    }
    .maxAndMin {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .details {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.25rem;
      .material-symbols-rounded {
        font-size: 2rem;
        font-variation-settings: 'FILL' 1, 'wght' 300;
      }
      .label {
        color: var(--widget-text-primary);
        font-size: 1.375rem;
      }
      .value {
        font-size: 1.375rem;
        font-weight: 600;
      }
    }
  }
  .weatherAnimationWrapper {
    .svg-container {
      position: initial;
      &.floating {
        position: absolute;
        left: auto;
        right: 0;
        top: 17.5%;
      }
      &.bouncing {
        position: absolute;
        left: auto;
        right: 0;
        top: 17.5%;
        top: 22.5%;
      }
    }
    .sunny {
      position: absolute;
      top: 20%;
      right: 3.5rem;
      z-index: 100;
    }
    .little_cloud {
      position: absolute;
      &.beside-sun {
        z-index: 999;
        right: 3rem;
        top: 4rem;
      }
    }
    .blur {
      position: absolute;
      right: 4rem;
      top: -40%;
    }
    .cloudy {
      top: 17%;
    }
    .snowdrop svg {
      width: 4rem;
      height: 4rem;
    }
  }
  &.medium {
    padding: 1.5rem;
    .info {
      height: 100%;
      display: flex;
      flex-direction: column;
      /* justify-content: space-between; */
    }
    .tempNow {
      font-size: 2.5rem;
      flex-grow: 1;
      display: flex;
      align-items: flex-end;
    }
    .details {
      .material-symbols-rounded {
        font-size: 1.5rem;
      }
      .label {
        font-size: 1.25rem;
      }
      .value {
        font-size: 1.25rem;
      }
    }
    .weatherAnimationWrapper {
      .svg-container {
        svg {
          width: 9rem;
          height: 9rem;
        }
        &.floating {
          right: 14%;
          top: 14.5%;
        }
        &.bouncing {
          position: absolute;
          left: auto;
          right: 13%;
          top: 17%;
        }
        .sunny {
          top: 15%;
          right: 7rem;
        }
        .little_cloud {
          position: absolute;
          &.beside-sun {
            z-index: 999;
            right: 7rem;
            top: 2rem;
          }
        }
        &.sunCloud {
          top: 2rem;
          right: 1rem;
        }
      }
      .rotating-sunCloud {
        top: 4% !important;
        width: 9rem;
        right: 30%;
      }
    }
  }
`;

const animateWeather = (showSvg: boolean, weather: string) => {
  const size = 320;
  const weatherIconStyle = { position: 'absolute', right: '0', top: '10%' } as React.CSSProperties;

  switch (weather) {
    case 'sunny': {
      return (
        <div className='container'>
          <div className='svg-container show-svg '>
            <SunnySvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'little_cloud': {
      return (
        <div className='container'>
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <LittleCloudySvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'blur': {
      return (
        <div className='container'>
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <BlurSvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'cloudy': {
      return (
        <div className='container'>
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <LittleCloudySvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'rain': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 95) {
        const randoHundo = Math.floor(Math.random() * 100);
        const randoFiver = Math.floor(Math.random() * 5) + 2;
        increment += randoFiver;

        const dropStyle = {
          left: `${increment}%`,
          backgroundColor: 'var(--text-primary)',
          animationDelay: `0.${randoHundo}s`,
          animationDuration: `0.8${randoHundo}s`,
        };

        newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
      }

      return (
        <div className='container'>
          {newDrops}
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <RainSvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'shower': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 95) {
        const randoHundo = Math.floor(Math.random() * 100);
        const randoFiver = Math.floor(Math.random() * 5) + 2;
        increment += randoFiver;

        const dropStyle = {
          left: `${increment}%`,
          backgroundColor: 'var(--text-primary)',
          animationDelay: `0.${randoHundo}s`,
          animationDuration: `0.3${randoHundo}s`,
        };

        newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
      }
      return (
        <div className='container'>
          {newDrops}
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <ShowerSvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'sleet': {
      const newDrops: any[] = [];
      let increment = 0;
      const types = ['raindrop', 'snowdrop'];

      while (increment < 100) {
        const randomType = types[Math.floor(Math.random() * types.length)];

        const randomDelay = (Math.random() * 5).toFixed(2);
        const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
        const randomLeftRight = Math.floor(Math.random() * 4);

        const snowSize = Math.floor(Math.random() * 5);
        const snowOpacity = (Math.random() * 1).toFixed(2);
        const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

        increment += randomUnit;

        let animationDuration;
        switch (randomType) {
          case 'raindrop':
            animationDuration = `${2 + Math.random()}s`;
            break;
          case 'snowdrop':
            animationDuration = `${5 + Math.random()}s`;
            break;
          default:
            animationDuration = `${5 + Math.random()}s`;
            break;
        }

        const rainParticle = {
          left: `${increment}%`,
          backgroundColor: 'var(--text-primary)',
          animationDelay: `${+randomDelay}s`,
          animationDuration,
          '--random-left-right': `${randomLeftRight}vw`,
        } as React.CSSProperties;

        const snowParticle = {
          width: `${snowSize}px`,
          height: `${snowSize}px`,
          borderRadius: `${snowSize}px`,
          backgroundColor: 'var(--text-primary)',
          opacity: `${Math.max(+snowOpacity, 0.3)}`,
          left: `${increment}%`,
          animationDelay: `${+randomDelay}s`,
          animationDuration: `${randomDuration}s`,
          '--random-left-right': `${randomLeftRight}vw`,
        } as React.CSSProperties;

        newDrops.push(<div className={randomType} style={randomType === 'raindrop' ? rainParticle : snowParticle} key={increment} />);
      }

      return (
        <div className='container'>
          {newDrops}
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <SleetSvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'snow': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 100) {
        const randomDelay = (Math.random() * 5).toFixed(2);
        const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
        const randomLeftRight = Math.floor(Math.random() * 4);

        const snowSize = Math.floor(Math.random() * 5);
        const snowOpacity = (Math.random() * 1).toFixed(2);
        const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

        increment += randomUnit;

        const snowParticle = {
          width: `${snowSize}px`,
          height: `${snowSize}px`,
          borderRadius: `${snowSize}px`,
          backgroundColor: `var(--text-primary)`,
          opacity: `${Math.max(+snowOpacity, 0.3)}`,
          left: `${increment}%`,
          animationDelay: `${+randomDelay}s`,
          animationDuration: `${randomDuration}s`,
          '--random-left-right': `${randomLeftRight}vw`,
        };

        newDrops.push(<div className='snowdrop' style={snowParticle as React.CSSProperties} key={increment} />);
      }

      return (
        <div className='container'>
          {newDrops}
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <SnowSvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    case 'sunCloud': {
      return (
        <div className='container'>
          <div className={`svg-container ${showSvg ? 'show-svg' : ''}`}>
            <LittleCloudySvgIcon size={size} style={weatherIconStyle} />
          </div>
        </div>
      );
    }

    default: {
      return (
        <div className='container'>
          <p className='no-weather-icon' style={{ position: 'absolute', top: '45%', right: '25%', fontSize: '1.25rem' }}>
            -
          </p>
        </div>
      );
    }
  }
};

interface Props {
  currentWeather: any;
  width: 'large' | 'medium';
}

const WeatherWideNow = ({ currentWeather, width }: Props) => {
  const { t } = useTranslation();

  const todayjs = dayjs(); // 현재 날짜와 시간을 가져옴
  let format = todayjs.format(`MM월 DD일 dddd`); // 기본 형식 설정

  // 언어가 영어일 경우 형식 변경
  if (i18n.language === 'en-US') {
    format = todayjs.locale('en').format(`MMMM D dddd`);
  } else {
    format = todayjs.locale('ko').format(`MM월 DD일 dddd`);
  }

  // SVG 표시 상태를 관리하는 상태
  const [showSvg, setShowSvg] = useState(false);

  // SVG 표시 상태를 관리하는 상태
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSvg(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Root className={`weatherNow ${width}`}>
      <div className='info'>
        <div className='details'>
          <span className='material-symbols-rounded'>distance</span>
          <span className='label'>{t('위치')}</span>
          <span className='value'>{t(currentWeather.district)}</span>
        </div>
        <div className='tempNow'>
          {currentWeather.tmp}
          <span className='unit'>°</span>
        </div>
        <div className='maxAndMin'>
          <div className='details min'>
            <span className='material-symbols-rounded'>arrow_downward</span>
            <div className='value'>
              {Math.floor(currentWeather.minTemp)}
              <span className='unit'>°</span>
            </div>
          </div>
          <span className='seperator' />
          <div className='details max'>
            <span className='material-symbols-rounded'>arrow_upward</span>
            <div className='value'>
              {Math.floor(currentWeather.maxTemp)}
              <span className='unit'>°</span>
            </div>
          </div>
        </div>
        <div className='details'>
          <span className='material-symbols-rounded'>umbrella</span>
          <span className='label'>{t('강수확률')}</span>
          <span className='value'>
            {currentWeather.rainPct}
            <span className='unit'>%</span>
          </span>
        </div>
        <div className='details'>
          <span className='material-symbols-rounded'>masks</span>
          <span className='label'>{t('미세먼지')}</span>
          <span className='value'>
            {currentWeather.pm10}
            <span className='unit'>㎍/㎥</span>
          </span>
        </div>
        <div className='details'>
          <span className='material-symbols-rounded'>air</span>
          <span className='label'>{t('풍속')}</span>
          <span className='value'>
            {currentWeather.wsd}
            <span className='unit'>m/s</span>
          </span>
        </div>
      </div>
      <div className='weatherAnimationWrapper'>{animateWeather(showSvg, currentWeather.sky)}</div>
    </Root>
  );
};

export default WeatherWideNow;
